import BasePeripheral from '@appblocks/codegeneration/Peripherals/_BasePeripheral';
import {
    SettingType, Setting, BlockType, ValidationMessage, ValidationMessageType,
    TibboDataType,
    SettingControlType,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import BaseSPIPeripheral from './_BaseSPIPeripheral';
import BaseFeature from '../BaseFeature';

export default class Ili9341 extends BaseSPIPeripheral {
    constructor() {
        super();
        this.id = 'ili9341';
        this.name = 'ILI9341 Display';
        this.requirements = ['zephyr'];

        const settings: Array<Setting> = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'IMAGES_GROUP',
                name: 'Images',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'RESETGPIOS',
                name: 'Reset GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'DCGPIOS',
                name: 'DC GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                refId: 'gpio',
                default: '',
            },
            {
                id: 'LCD_SCREEN_WIDGET_COLUMNS',
                name: 'Width',
                settingType: SettingType.NUMBER,
                group: 'GENERAL',
                required: true,
                default: '0',
            },
            {
                id: 'LCD_SCREEN_WIDGET_ROWS',
                name: 'Height',
                settingType: SettingType.NUMBER,
                group: 'GENERAL',
                required: true,
                default: '0',
            },
            {
                id: 'ROTATION',
                name: 'Rotation',
                settingType: SettingType.NUMBER,
                group: 'GENERAL',
                required: true,
                default: '0',
            },
            {
                id: 'SCREENS',
                name: 'Screens',
                settingType: SettingType.ARRAY,
                required: true,
                group: 'GENERAL',
                refId: 'display_screen',
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'IMAGES',
                name: 'Images',
                settingType: SettingType.ARRAY,
                refId: 'lcd_image',
                required: true,
                group: 'IMAGES_GROUP',
                default: new Array<BaseFeature>(0),
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDependencies(): string[] {
        return [
            'display_lib',
        ];
    }

    getCodeBlocks() {
        const blocks = [];

        const project = this.project;
        const NAME = this.getSettingValue('NAME');
        const SPI = this.getSettingValue('SPI');
        if (!SPI) {
            return SPI;
        }
        const SPIFeature = project.getFeature(SPI);
        const LCD_SCREEN_WIDGET_COLUMNS = this.getSettingValue('LCD_SCREEN_WIDGET_COLUMNS');
        const LCD_SCREEN_WIDGET_ROWS = this.getSettingValue('LCD_SCREEN_WIDGET_ROWS');
        const ADDRESS = this.getSettingValue('ADDRESS');
        const ROTATION = this.getSettingValue('ROTATION');

        // const GND = this.getSettingValue('GND');
        // const VCC = this.getSettingValue('VCC');
        const SCREENS = this.getSettingValue('SCREENS');
        let dcGPIOSCode = '';

        const DCGPIO = project.getFeature(this.getSettingValue('DCGPIOS'));

        if (DCGPIO) {
            const pin = Peripherals.getGPIOPeripheral(DCGPIO.getSettingValue('PIN'), this.project.device.id);

            dcGPIOSCode = `dc-gpios = <&${pin?.peripheralPort} ${pin.num} ${DCGPIO.getFlags()}>;`;
        }
        let resetGPIOSCode = '';
        const RESETGPIO = project.getFeature(this.getSettingValue('RESETGPIOS'));
        if (RESETGPIO) {
            const pin = Peripherals.getGPIOPeripheral(RESETGPIO.getSettingValue('PIN'), this.project.device.id);

            resetGPIOSCode = `reset-gpios = <&${pin?.peripheralPort} ${pin.num} ${RESETGPIO.getFlags()}>;`;
        }

        blocks.push({
            location: 'APP_OVERLAY_INCLUDES',
            template: `
#include <zephyr/dt-bindings/display/ili9xxx.h>
        `,
        });

        blocks.push({
            location: 'APP_OVERLAY_DTS',
            template: `
mipi_dbi {
    compatible = "zephyr,mipi-dbi-spi";
    ${dcGPIOSCode}
    ${resetGPIOSCode}
    spi-dev = <&${SPIFeature.getSettingValue('PORT')}>;
    write-only;
    #address-cells = <1>;
    #size-cells = <0>;

    ili9341: ili9341@${ADDRESS} {
        compatible = "ilitek,ili9341";
        mipi-max-frequency = <30000000>;
        reg = <${ADDRESS}>;
        // vin-supply = <&lcd_bg>;
        pixel-format = <ILI9XXX_PIXEL_FORMAT_RGB565>;
        // display-inversion;
        width = <${LCD_SCREEN_WIDGET_COLUMNS}>;
        height = <${LCD_SCREEN_WIDGET_ROWS}>;
        rotation = <${ROTATION}>;
    };
};
        `,
        });

        blocks.push({
            location: 'APP_OVERLAY_CHOSEN',
            template: `
zephyr,display = &ili9341;`,
        });

        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `const struct device *${NAME}_device;`,
        });

        return blocks;
    }
}
